import {
  useQuery,
  useMutation,
} from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import {
  downloadPriceVarianceCsv,
  fetchPriceVarianceGrouped,
  type PriceVarianceFilters,
} from "../api/reports";
import {
  fetchPriceAnomalyRule,
  updatePriceAnomalyRule,
} from "../api/priceAnomalyRule";
import type {
  PriceAnomalyComparisonBasis,
  PriceAnomalyRuleResponse,
} from "../api/priceAnomalyRule";
import FilterDropdown from "../components/FilterDropdown";
import Select from "../components/Select";
import { formatEuro, toNumber } from "../lib/format";
import { priceAnomalyRuleText } from "../lib/anomalyRule";
import { useOverflowGuard } from "../lib/useOverflowGuard";
import { AnalysisExplanation, AnalysisFilterBar, effectivePeriod } from "./priceVarianceReport/AnalysisFilters";
import AnalysisTabs from "./priceVarianceReport/AnalysisTabs";
import { MinOverpayControl } from "./priceVarianceReport/MinOverpayControl";
import { MinReferenceCountControl } from "./priceVarianceReport/MinReferenceCountControl";
import OverpayTop10Tab from "./priceVarianceReport/OverpayTop10Tab";
import PriceReportWorkflow from "./priceVarianceReport/PriceReportWorkflow";
import PurchasesTab from "./priceVarianceReport/PurchasesTab";
import { usePurchasesSummary } from "./priceVarianceReport/purchases";
import { ReferenceQualityChoice } from "./priceVarianceReport/ReferenceQualityChoice";
import { PriceVarianceFiltersPanel } from "./priceVarianceReport/ReportFilters";
import { GROUP_OPTIONS } from "./priceVarianceReport/constants";
import { usePriceVarianceReportData } from "./priceVarianceReport/data";
import { usePriceVarianceReportState } from "./priceVarianceReport/state";
import type { GroupMode, PriceVariancePreset, PriceVarianceView } from "./priceVarianceReport/types";
import {
  VarianceFlatTable,
  VarianceGroupedTable,
} from "./priceVarianceReport/VarianceTables";

type PresetChoice = "all" | PriceVariancePreset;

const PRESET_OPTIONS: { value: PresetChoice; label: string }[] = [
  { value: "all", label: "Preisabweichungen" },
  { value: "historical_spikes", label: "Historische Preisspitzen" },
];

// Filter keys the Analyse writes explicitly. A link without them is a fresh
// entry and gets the Analyse defaults; links from elsewhere keep theirs.
const ANALYSIS_STATE_KEY = "min_delta_pct";

export default function PriceVarianceReportPage() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  // Der geführte Preisbericht (aus den Kostenstellen) bleibt eigenständig.
  if (params.has("report")) return <PriceReportWorkflow />;
  return <AnalysisPage />;
}

/**
 * Analyse (Beta-Feedback 2026-10-07): Filter über drei Reitern –
 * „Mehrkosten Top 10“, „Alle Mehrkosten“ und „Einkaufsvolumen“.
 */
function AnalysisPage() {
  const location = useLocation();
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const ruleQuery = useQuery({
    queryKey: ["price-anomaly-rule"],
    queryFn: fetchPriceAnomalyRule,
  });
  const filterButtonRef = useRef<HTMLButtonElement | null>(null);
  const filterReturnFocusRef = useRef<HTMLElement | null>(null);
  function openFilterDrawer(opener: HTMLButtonElement) {
    filterReturnFocusRef.current = opener;
    setFilterDrawerOpen(true);
  }
  const {
    preset,
    setPreset,
    filters,
    effectiveFilters: requestedFilters,
    sort,
    setSort,
    groupedSort,
    setGroupedSort,
    groupMode: requestedGroupMode,
    changeGroupMode,
    deliveryKind,
    setDeliveryKind,
    expanded,
    updateFilter,
    view,
    setView,
    period: requestedPeriod,
    setPeriod,
    applyAnalysisDefaults,
    adoptLegacyList: setLegacyList,
    toggleGroupRow,
    groupKeyFor,
  } = usePriceVarianceReportState(ruleQuery.data?.effective);
  const incomingParams = new URLSearchParams(location.search);
  const fresh = !incomingParams.has(ANALYSIS_STATE_KEY);
  // The old positions list wrote every filter but never `view` or `period`;
  // such links (e.g. back from a Beleg) return to "Alle Mehrkosten" as they were.
  const legacyList = !fresh && !incomingParams.has("period");
  useEffect(() => {
    if (legacyList) {
      setLegacyList();
      return;
    }
    // Fresh entry or a link from elsewhere with a few filters: start from the
    // Analyse defaults and keep every filter the link names explicitly.
    if (fresh) {
      const keep: PriceVarianceFilters = {};
      const named = (key: string) => incomingParams.has(key);
      if (named("supplier")) keep.supplier = filters.supplier;
      if (named("manufacturer_id")) keep.manufacturer_id = filters.manufacturer_id;
      if (named("project")) keep.project = filters.project;
      if (named("article")) keep.article = filters.article;
      if (named("delivery_address_key")) keep.delivery_address_key = filters.delivery_address_key;
      if (named("online_user")) keep.online_user = filters.online_user;
      if (named("is_warehouse")) keep.is_warehouse = filters.is_warehouse;
      if (named("price_anomaly_status")) keep.price_anomaly_status = filters.price_anomaly_status;
      if (named("min_overpay_abs")) keep.min_overpay_abs = filters.min_overpay_abs;
      if (named("reference_quality")) keep.reference_quality = filters.reference_quality;
      if (named("min_reference_count")) keep.min_reference_count = filters.min_reference_count;
      if (named("from") || named("to")) {
        // An old link with its own dates keeps them as "Eigener Zeitraum".
        keep.from = filters.from;
        keep.to = filters.to;
      }
      // Links from elsewhere (Artikel, Belege, Kostenstellen) always meant the
      // list of positions; without any filter the Analyse opens on the Top 10.
      const linked = Object.keys(keep).length > 0 || named("preset");
      applyAnalysisDefaults(keep, {
        ...(named("group_by") ? { groupMode: requestedGroupMode } : {}),
        ...(named("preset") ? { preset } : {}),
        ...(named("sort") ? { sort } : {}),
        ...(named("grouped_sort") ? { groupedSort } : {}),
      }, named("view") ? undefined : linked ? "positions" : "overview");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fresh, legacyList]);
  const period = effectivePeriod(requestedPeriod, filters);
  const analysisMode = useMemo(() => ({ namedPeriod: period !== "custom" }), [period]);
  const referenceCountsAvailable = ruleQuery.data?.reference_counts_available !== false;
  const effectiveFilters = useMemo(() => referenceCountsAvailable
    ? requestedFilters
    : { ...requestedFilters, reference_quality: undefined, min_reference_count: undefined },
    [referenceCountsAvailable, requestedFilters]);
  const insufficient = referenceCountsAvailable && filters.reference_quality === "insufficient";
  const groupMode = insufficient ? "none" : requestedGroupMode;
  const ready = ruleQuery.data !== undefined && !fresh && !legacyList;
  const {
    referenceSummaryQuery,
    varianceQuery,
    groupedQuery,
    usersQuery,
    manufacturerRollupQuery,
    supplierRollupQuery,
    projectsQuery,
    projectByKey,
    flatItems,
    flatTotal,
    flatHasData,
    exportFilters,
    manufacturerOptions,
    supplierOptions,
    projectOptions,
  } = usePriceVarianceReportData({
    enabled: ready && view === "positions",
    rollupsEnabled: ready,
    effectiveFilters,
    groupMode,
    sort,
    groupedSort,
  });
  // Kennzahl der Mehrkosten-Reiter: dieselben Filter wie die Liste.
  const totalsQuery = useQuery({
    queryKey: ["reports", "analysis-totals", effectiveFilters],
    queryFn: () => fetchPriceVarianceGrouped({ ...effectiveFilters, group_by: "supplier" }),
    enabled: ready && view !== "purchases",
  });
  const articleCountQuery = useQuery({
    queryKey: ["reports", "analysis-top-articles", effectiveFilters, "overpay"],
    queryFn: () => fetchPriceVarianceGrouped({ ...effectiveFilters, group_by: "article", sort: "overpay_desc" }),
    enabled: ready && view !== "purchases",
  });
  const purchases = usePurchasesSummary(effectiveFilters, ready && view === "purchases");
  const currentPath = `${location.pathname}${location.search}`;
  const advancedCount = [
    Boolean(filters.online_user),
    filters.price_anomaly_status !== undefined,
    Boolean(filters.article?.length),
    Boolean(filters.project?.length),
    Boolean(filters.delivery_address_key?.length),
    deliveryKind !== "beides",
    period === "custom",
  ].filter(Boolean).length;
  const changed = period !== "12m" || filters.min_delta_pct != null || Boolean(filters.supplier?.length)
    || Boolean(filters.manufacturer_id?.length) || advancedCount > 0 || preset !== null || filters.min_overpay_abs != null;
  const closeFilterDrawer = useCallback(() => {
    setFilterDrawerOpen(false);
    window.requestAnimationFrame(() => {
      const opener = filterReturnFocusRef.current;
      (opener?.isConnected ? opener : filterButtonRef.current)?.focus();
    });
  }, []);

  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const hasMoreVariance = Boolean(varianceQuery.hasNextPage);
  const loadMoreVariance = useCallback(() => {
    if (!varianceQuery.hasNextPage || varianceQuery.isFetchingNextPage) return;
    void varianceQuery.fetchNextPage();
  }, [varianceQuery]);

  useEffect(() => {
    if (!hasMoreVariance) return;
    const target = loadMoreRef.current;
    if (!target) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          loadMoreVariance();
        }
      },
      { rootMargin: "280px" },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [hasMoreVariance, loadMoreVariance]);

  const tableScrollRef = useOverflowGuard<HTMLDivElement>(
    groupMode === "none" ? "price-variance-flat" : `price-variance-grouped-${groupMode}`,
  );

  const activeRule = ruleQuery.data;
  const header = (
    <header>
      <h1 className="text-2xl font-semibold text-slate-900">Analyse</h1>
      <p className="mt-1 text-sm text-slate-600">Was haben wir wann eingekauft – und wo zahlen wir mehr als früher?</p>
    </header>
  );
  if (!activeRule) {
    return (
      <main className="mx-auto max-w-7xl px-6 py-6">
        {header}
        {ruleQuery.isError ? (
          <section
            role="alert"
            className="mt-5 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800"
          >
            <p className="font-medium">
              Die aktive Preisprüfungsregel konnte nicht geladen werden.
            </p>
            <p className="mt-1 text-xs leading-5">
              Die Auswertung bleibt angehalten, damit keine abweichende
              Standardschwelle angezeigt oder exportiert wird.
            </p>
            <button
              type="button"
              onClick={() => void ruleQuery.refetch()}
              className="mt-2 h-9 rounded-md border border-rose-300 bg-white px-3 text-sm font-medium text-rose-800 shadow-sm hover:bg-rose-100"
            >
              Erneut laden
            </button>
          </section>
        ) : (
          <section
            aria-live="polite"
            className="mt-5 rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600 shadow-sm"
          >
            Lade Prüfregel …
          </section>
        )}
      </main>
    );
  }

  const selectOnly = (key: "supplier" | "manufacturer_id", id: string) => {
    const current = filters[key] ?? [];
    updateFilter(key, current.length === 1 && current[0] === id ? undefined : [id]);
  };
  const positionsHref = (() => {
    const next = new URLSearchParams(location.search);
    next.set("view", "positions");
    return `${location.pathname}?${next.toString()}`;
  })();
  const overpayTotal = (totalsQuery.data?.items ?? []).reduce((sum, row) => sum + Math.max(0, toNumber(row.total_overpay) ?? 0), 0);
  const overpayLines = (totalsQuery.data?.items ?? []).reduce((sum, row) => sum + (row.overpay_line_count ?? 0), 0);
  const articleCount = (articleCountQuery.data?.items ?? []).filter((row) => (toNumber(row.total_overpay) ?? 0) > 0).length;
  // Export gehört zur Liste, nicht zu den Filtern (#630); er bleibt auch ohne
  // Angaben zur Vergleichsbasis erreichbar.
  const exportLink = <PriceVarianceExportButton filters={exportFilters} />;
  return (
    <main className="mx-auto max-w-7xl px-6 py-6">
      {header}
      <AnalysisFilterBar
        view={view}
        period={period}
        filters={filters}
        rulePct={Number(activeRule.effective.threshold_pct)}
        supplierOptions={supplierOptions}
        manufacturerOptions={manufacturerOptions}
        advancedCount={advancedCount}
        changed={changed}
        filterButtonRef={filterButtonRef}
        drawerOpen={filterDrawerOpen}
        onPeriod={(next) => {
          setPeriod(next);
          if (next === "custom" && filterButtonRef.current) openFilterDrawer(filterButtonRef.current);
        }}
        onDelta={(next) => updateFilter("min_delta_pct", next ?? undefined)}
        onSupplier={(next) => updateFilter("supplier", next ? [next] : undefined)}
        onManufacturer={(next) => updateFilter("manufacturer_id", next ? [next] : undefined)}
        onOpenDrawer={openFilterDrawer}
        onReset={() => applyAnalysisDefaults()}
      />
      <AnalysisExplanation
        view={view}
        period={period}
        filters={filters}
        supplierOptions={supplierOptions}
        manufacturerOptions={manufacturerOptions}
        advancedCount={advancedCount}
        rule={activeRule.effective}
        canManageRule={activeRule.can_manage}
      />
      <PriceVarianceFiltersPanel
        analysis={analysisMode}
        open={filterDrawerOpen}
        onClose={closeFilterDrawer}
        preset={preset}
        filters={filters}
        updateFilter={(key, value) => {
          // Von/Bis im Drawer machen den Zeitraum zu einem eigenen.
          if ((key === "from" || key === "to") && period !== "custom") setPeriod("custom");
          updateFilter(key, value);
        }}
        users={usersQuery.data?.items}
        projectOptions={projectOptions}
        projectsLoading={projectsQuery.isLoading}
        manufacturerOptions={manufacturerOptions}
        manufacturersLoading={manufacturerRollupQuery.isLoading}
        supplierOptions={supplierOptions}
        suppliersLoading={supplierRollupQuery.isLoading}
        deliveryKind={deliveryKind}
        onDeliveryKindChange={setDeliveryKind}
        onResetAll={() => {
          applyAnalysisDefaults();
          closeFilterDrawer();
        }}
      />

      <AnalysisKpi
        view={view}
        loading={view === "purchases" ? !purchases.summary : totalsQuery.isPending || articleCountQuery.isPending}
        overpay={overpayTotal}
        overpayLines={overpayLines}
        articleCount={articleCount}
        purchases={purchases.summary}
      />

      <AnalysisTabs
        active={view}
        onSelect={(next: PriceVarianceView) => setView(next)}
        counts={{ positions: view === "positions" ? flatTotal : null }}
      />

      {!ready ? (
        <p role="status" className="mt-6 text-sm text-slate-600">Lade Analyse …</p>
      ) : view === "overview" ? (
        <div className="mt-5">
          <OverpayTop10Tab
            filters={effectiveFilters}
            supplierRows={supplierRollupQuery.data?.items}
            supplierLoading={supplierRollupQuery.isPending}
            supplierError={supplierRollupQuery.isError}
            manufacturerRows={manufacturerRollupQuery.data?.items}
            manufacturerLoading={manufacturerRollupQuery.isPending}
            manufacturerError={manufacturerRollupQuery.isError}
            onFilter={selectOnly}
            positionsHref={positionsHref}
          />
        </div>
      ) : view === "purchases" ? (
        <div className="mt-5">
          <PurchasesTab filters={effectiveFilters} onFilter={selectOnly} />
        </div>
      ) : (
        <section role="tabpanel" aria-label="Alle Mehrkosten" className="mt-5">
          <div className="flex flex-wrap items-center gap-2">
            <FilterDropdown<PresetChoice>
              label="Ansicht"
              ariaLabel="Ansicht wählen"
              options={PRESET_OPTIONS}
              value={preset ?? "all"}
              defaultValue="all"
              showValue
              onChange={(next) => setPreset(next === "historical_spikes" ? next : null)}
            />
            <MinOverpayControl
              insufficientReferences={insufficient}
              value={filters.min_overpay_abs ?? null}
              onChange={(next) => updateFilter("min_overpay_abs", next ?? undefined)}
            />
            {preset === "historical_spikes" && !insufficient && referenceCountsAvailable ? (
              <MinReferenceCountControl
                value={filters.min_reference_count ?? null}
                onChange={(next) => updateFilter("min_reference_count", next ?? undefined)}
              />
            ) : null}
            <FilterDropdown<GroupMode>
              label="Gruppierung"
              ariaLabel="Gruppierung wählen"
              options={GROUP_OPTIONS}
              value={insufficient ? "none" : groupMode}
              defaultValue="none"
              showValue
              disabled={insufficient}
              onChange={changeGroupMode}
            />
          </div>
          {referenceCountsAvailable ? <ReferenceQualityChoice
            aside={exportLink}
            value={insufficient ? "insufficient" : "sufficient"}
            onChange={(next) => updateFilter("reference_quality", next)}
            query={referenceSummaryQuery}
          /> : <div className="mt-4 flex flex-wrap items-start justify-between gap-3">
            <p className="max-w-3xl text-sm text-slate-600">Dieser Demo-Datensatz enthält noch keine Angaben zur Zahl früherer Vergleichsbelege. Die vorhandenen Preisvergleiche bleiben sichtbar; eine Einordnung nach Vergleichsbasis ist hier noch nicht möglich.</p>
            {exportLink}
          </div>}
          <div className="mt-4">
            {groupMode === "none" ? (
              <VarianceFlatTable
                items={flatItems}
                hasData={flatHasData}
                isFetching={varianceQuery.isFetching}
                isFetchingNextPage={varianceQuery.isFetchingNextPage}
                isError={varianceQuery.isError}
                error={varianceQuery.error as Error | null}
                onRetry={() => void varianceQuery.refetch()}
                onAdjustFilters={openFilterDrawer}
                total={flatTotal}
                hasMore={hasMoreVariance}
                onLoadMore={loadMoreVariance}
                loadMoreRef={loadMoreRef}
                projectByKey={projectByKey}
                sort={sort}
                onSortChange={setSort}
                scrollRef={tableScrollRef}
                fromPath={currentPath}
                insufficientReferences={insufficient}
              />
            ) : (
              <VarianceGroupedTable
                data={groupedQuery.data}
                isFetching={groupedQuery.isFetching}
                isError={groupedQuery.isError}
                error={groupedQuery.error as Error | null}
                onRetry={() => void groupedQuery.refetch()}
                onAdjustFilters={openFilterDrawer}
                axes={groupMode}
                expanded={expanded}
                toggleGroupRow={toggleGroupRow}
                groupKeyFor={groupKeyFor}
                filters={effectiveFilters}
                flatSort={sort}
                projectByKey={projectByKey}
                sort={groupedSort}
                onSortChange={setGroupedSort}
                scrollRef={tableScrollRef}
                fromPath={currentPath}
              />
            )}
          </div>
        </section>
      )}
    </main>
  );
}

/** Eine Zeile mit der Antwort: Mehrkosten oder Einkaufsvolumen der Auswahl. */
function AnalysisKpi({
  view,
  loading,
  overpay,
  overpayLines,
  articleCount,
  purchases,
}: {
  view: PriceVarianceView;
  loading: boolean;
  overpay: number;
  overpayLines: number;
  articleCount: number;
  purchases: ReturnType<typeof usePurchasesSummary>["summary"];
}) {
  if (loading) {
    return <p className="mt-5 h-9 text-sm text-slate-600" aria-live="polite">Lade Kennzahlen …</p>;
  }
  if (view === "purchases") {
    if (!purchases) return null;
    return (
      <p className="mt-5 flex flex-wrap items-baseline gap-x-3 gap-y-1" data-testid="analysis-kpi">
        <span className="text-2xl font-semibold tabular-nums tracking-tight text-slate-900">{formatEuro(purchases.total)}</span>
        <span className="text-sm text-slate-600">
          Einkaufsvolumen · {purchases.lines.toLocaleString("de-AT")} Positionen · {purchases.documents.toLocaleString("de-AT")} Belege · im Schnitt {formatEuro(purchases.average)} pro Monat
        </span>
      </p>
    );
  }
  return (
    <p className="mt-5 flex flex-wrap items-baseline gap-x-3 gap-y-1" data-testid="analysis-kpi">
      <span className="text-2xl font-semibold tabular-nums tracking-tight text-slate-900">{formatEuro(overpay)}</span>
      <span className="text-sm text-slate-600">
        Mehrkosten · {overpayLines.toLocaleString("de-AT")} teurere {overpayLines === 1 ? "Kauf" : "Käufe"} · {articleCount.toLocaleString("de-AT")} {articleCount === 1 ? "Artikel" : "Artikel"}
      </span>
    </p>
  );
}

export function PriceAnomalyRuleCard({
  rule,
  onSaved,
}: {
  rule: PriceAnomalyRuleResponse;
  onSaved: (next: PriceAnomalyRuleResponse) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [basis, setBasis] = useState<PriceAnomalyComparisonBasis>(
    rule.configured.comparison_basis,
  );
  const [threshold, setThreshold] = useState(rule.configured.threshold_pct);
  const [minimum, setMinimum] = useState(
    rule.configured.minimum_overpay_abs ?? "",
  );
  const [minimumReferences, setMinimumReferences] = useState(String(Math.max(2, rule.configured.minimum_reference_count)));
  const mutation = useMutation({
    mutationFn: () =>
      updatePriceAnomalyRule({
        request_id: globalThis.crypto.randomUUID(),
        expected_revision: rule.configured.revision,
        comparison_basis: basis,
        threshold_pct: Number(threshold),
        minimum_overpay_abs: minimum.trim() ? Number(minimum) : null,
        minimum_reference_count: Number(minimumReferences),
      }),
    onSuccess: (next) => {
      onSaved(next);
      setEditing(false);
    },
  });
  const valid =
    Number.isInteger(Number(minimumReferences)) && Number(minimumReferences) >= 2 && Number(minimumReferences) <= 1000 &&
    Number.isFinite(Number(threshold)) &&
    Number(threshold) > 0 &&
    Number(threshold) <= 1000 &&
    (!minimum.trim() ||
      (Number.isFinite(Number(minimum)) &&
        Number(minimum) > 0 &&
        Number(minimum) <= 999_999_999_999.99));

  function resetDraft() {
    setBasis(rule.configured.comparison_basis);
    setThreshold(rule.configured.threshold_pct);
    setMinimum(rule.configured.minimum_overpay_abs ?? "");
    setMinimumReferences(String(Math.max(2, rule.configured.minimum_reference_count)));
  }

  useEffect(() => {
    setBasis(rule.configured.comparison_basis);
    setThreshold(rule.configured.threshold_pct);
    setMinimum(rule.configured.minimum_overpay_abs ?? "");
    setMinimumReferences(String(Math.max(2, rule.configured.minimum_reference_count)));
  }, [rule.configured]);

  return (
    <section
      aria-labelledby="price-anomaly-rule-title"
      className="mt-3 rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-sm"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 max-w-5xl">
          <div className="flex flex-wrap items-center gap-2">
            <h2
              id="price-anomaly-rule-title"
              className="text-sm font-semibold text-slate-900"
            >
              Aktive Preisprüfungsregel
            </h2>
            {rule.refresh_pending ? (
              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                Änderung wartet auf Datenaktualisierung
              </span>
            ) : (
              <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800">
                Aktiv
              </span>
            )}
          </div>
          <p className="mt-1 text-sm leading-5 text-slate-600">
            {priceAnomalyRuleText(rule.effective)}
          </p>
          {rule.refresh_pending ? (
            <p className="mt-1 text-xs leading-5 text-amber-800">
              Gespeichert ist bereits Revision {rule.configured.revision}. Sie
              wird automatisch beim nächsten regulären Datenlauf wirksam;
              bestehende Preisprüfungen bleiben bis dahin unverändert.
            </p>
          ) : null}
        </div>
        {rule.can_manage && !editing ? (
          <button
            type="button"
            onClick={() => {
              resetDraft();
              setEditing(true);
            }}
            className="h-9 rounded-md border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50"
          >
            Regel bearbeiten
          </button>
        ) : null}
      </div>

      {editing ? (
        <form
          className="mt-4 grid gap-3 border-t border-slate-200 pt-4 md:grid-cols-2 xl:grid-cols-[minmax(14rem,1fr)_8rem_11rem_10rem_auto] md:items-end"
          onSubmit={(event) => {
            event.preventDefault();
            if (valid) mutation.mutate();
          }}
        >
          <label className="text-xs font-medium text-slate-700">
            Vergleichszeitraum
            <Select
              containerClassName="mt-1 block"
              value={basis}
              onChange={(event) =>
                setBasis(event.target.value as PriceAnomalyComparisonBasis)
              }
            >
              <option value="prior_12m_fallback">
                12 Monate, dann 24, dann alles
              </option>
              <option value="prior_24m_fallback">
                24 Monate, dann alles
              </option>
              <option value="prior_all_history">Gesamte frühere Historie</option>
            </Select>
          </label>
          <label className="text-xs font-medium text-slate-700">
            Abweichung in %
            <input
              type="number"
              min="0.001"
              max="1000"
              step="0.001"
              required
              value={threshold}
              onChange={(event) => setThreshold(event.target.value)}
              className="mt-1 h-9 w-full rounded-md border border-slate-200 bg-white px-3 text-sm tabular-nums outline-none focus:border-emerald-500"
            />
          </label>
          <label className="text-xs font-medium text-slate-700">
            Frühere Positionen, mindestens
            <input type="number" min="2" max="1000" step="1" required value={minimumReferences}
              onChange={(event) => setMinimumReferences(event.target.value)}
              className="mt-1 h-9 w-full rounded-md border border-slate-200 bg-white px-3 text-sm tabular-nums outline-none focus:border-emerald-700" />
          </label>
          <label className="text-xs font-medium text-slate-700">
            Mindestmehrkosten (optional)
            <input
              type="number"
              min="0.01"
              max="999999999999.99"
              step="0.01"
              value={minimum}
              onChange={(event) => setMinimum(event.target.value)}
              placeholder="Belegwährung"
              className="mt-1 h-9 w-full rounded-md border border-slate-200 bg-white px-3 text-sm tabular-nums outline-none focus:border-emerald-500"
            />
          </label>
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={!valid || mutation.isPending}
              className="h-9 rounded-md bg-emerald-600 px-3 text-sm font-medium text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {mutation.isPending ? "Speichert…" : "Speichern"}
            </button>
            <button
              type="button"
              onClick={() => {
                resetDraft();
                setEditing(false);
              }}
              disabled={mutation.isPending}
              className="h-9 rounded-md px-3 text-sm text-slate-600 hover:bg-slate-100"
            >
              Abbrechen
            </button>
          </div>
          {mutation.error ? (
            <p role="alert" className="text-xs text-rose-700 md:col-span-4">
              {mutation.error instanceof Error
                ? mutation.error.message
                : "Die Regel konnte nicht gespeichert werden."}
            </p>
          ) : null}
        </form>
      ) : null}
    </section>
  );
}

/** Lädt die gefilterte Liste als Datei – angemeldet bzw. in der Demo lokal (#632-Review R2). */
function PriceVarianceExportButton({ filters }: { filters: Parameters<typeof downloadPriceVarianceCsv>[0] }) {
  const [state, setState] = useState<"idle" | "busy" | "error">("idle");
  return (
    <span className="flex shrink-0 flex-wrap items-center gap-2">
      <button
        type="button"
        disabled={state === "busy"}
        onClick={async () => {
          setState("busy");
          try {
            await downloadPriceVarianceCsv(filters);
            setState("idle");
          } catch (reason) {
            setState(reason instanceof DOMException && reason.name === "AbortError" ? "idle" : "error");
          }
        }}
        className="inline-flex h-9 items-center rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-60"
      >
        {state === "busy" ? "Export läuft …" : "Export für Excel"}
      </button>
      {state === "error" ? <span role="alert" className="text-xs text-rose-700">Export fehlgeschlagen. Bitte erneut versuchen.</span> : null}
    </span>
  );
}
